import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import {
    closedContourStackMembership,
    PointMembership,
} from '~/domain/geometry/contourStackMembership';
import {
    BodyMaskBasis,
    BodyOutlineCandidate,
    bodyOutlineCandidates,
} from '~/domain/patient/bodyOutline';
import { Image3D } from '~/domain/patient/image3d';
import { detachedContourResultsState } from '~/state/atoms/detachedContourResultsAtom';
import { generateBodyMembership } from './generatedBodyMembership';
import { logErrorName } from '~/utils/logErrorName';

export interface BodyMask {
    readonly insideBody: PointMembership | undefined;
    readonly basis: BodyMaskBasis;
}

// One segmentation per scan, shared by every view that shows it.
const generated = new WeakMap<Image3D, Promise<PointMembership | undefined>>();

function generatedBodyOf(image: Image3D) {
    let pending = generated.get(image);
    if (!pending) {
        pending = generateBodyMembership(image).catch((error) => {
            logErrorName(
                'Body segmentation for the deformation mask failed:',
                error
            );
            return undefined;
        });
        generated.set(image, pending);
    }
    return pending;
}

function firstMembership(
    candidates: readonly BodyOutlineCandidate[]
):
    | { membership: PointMembership; kind: BodyOutlineCandidate['kind'] }
    | undefined {
    for (const candidate of candidates) {
        const membership = closedContourStackMembership(candidate.contours);
        if (membership) {
            return { membership, kind: candidate.kind };
        }
    }
    return undefined;
}

// The scan's own body, never a guess: an independent outline, else the scan segmented, else the deformed body.
export function useBodyOutline(
    image: Image3D | undefined,
    needed: boolean
): BodyMask {
    const patient = useSessionOrNull()?.patient;
    const detached = useRecoilValue(detachedContourResultsState);

    const { independent, deformed } = useMemo(() => {
        const contourPlusSets = Object.values(detached).flatMap((result) =>
            result.kind === 'ready' ? [result.structureSet] : []
        );
        const candidates = bodyOutlineCandidates(
            patient?.structureSets ?? [],
            contourPlusSets,
            image?.dicomUid
        );
        return {
            independent: firstMembership(
                candidates.filter((candidate) => candidate.kind !== 'model')
            ),
            deformed: firstMembership(
                candidates.filter((candidate) => candidate.kind === 'model')
            ),
        };
    }, [patient, detached, image]);

    const segmentable = !!image?.isHuQuantified;
    const wantsGenerated = needed && !independent && segmentable;
    const [segmented, setSegmented] = useState<{
        image: Image3D;
        membership: PointMembership | undefined;
    }>();

    useEffect(() => {
        if (!wantsGenerated || !image) {
            return;
        }
        let current = true;
        void generatedBodyOf(image).then((membership) => {
            if (current) {
                setSegmented({ image, membership });
            }
        });
        return () => {
            current = false;
        };
    }, [wantsGenerated, image]);

    if (independent) {
        return { insideBody: independent.membership, basis: independent.kind };
    }
    if (wantsGenerated && image) {
        if (segmented?.image !== image) {
            return { insideBody: undefined, basis: 'computing' };
        }
        if (segmented.membership) {
            return { insideBody: segmented.membership, basis: 'generated' };
        }
    }
    if (deformed) {
        return { insideBody: deformed.membership, basis: 'model' };
    }
    return { insideBody: undefined, basis: 'none' };
}
